import { useQuery } from 'react-query';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import { toTripState } from '../logic/toTripState';
import { useTripFilters } from './useTripStoreSelectors';

interface UseTripVersionPreviewParams {
  tripId: string;
  versionId?: string;
}

/**
 * Reads one version of a trip, formatted like the live trip, without touching
 * the store: the compare panel shows it beside the trip being edited. The
 * answer is never reused, so opening the panel always reads the version as it
 * is now.
 */
export function useTripVersionPreview({
  tripId,
  versionId,
}: UseTripVersionPreviewParams) {
  const { api, getFactsheetUrl, locale: appLocale } = useTripDesignerConfig();
  const tripFilters: any = useTripFilters();

  const query = useQuery(
    ['tripVersionPreview', tripId, versionId],
    async () => {
      const trip: any = (await api.getTrip({ tripId, versionId })).data;
      const locale = tripFilters?.locale ?? trip?.locale ?? appLocale;
      const { days, experiences, hotels } = await toTripState({
        trip,
        api,
        locale,
      });

      return { trip, days, experiences, hotels, locale };
    },
    { retry: false, cacheTime: 0, refetchOnWindowFocus: false },
  );

  return {
    trip: query.data?.trip,
    days: query.data?.days ?? [],
    experiences: query.data?.experiences ?? {},
    hotels: query.data?.hotels ?? {},
    locale: query.data?.locale,
    isLoading: query.isLoading,
    getFactsheetUrl,
  };
}
